
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Edit, Trash2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
import ImageGallery, { useImageGallery } from "../../components/common/ImageGallery";
// Image removed - use img tag;

export default function AdminProductsPage() {
  const { token } = useAuthStore();
  const location = useLocation();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const productsPerPage = 30;
  const { isOpen, initialIndex, openGallery, closeGallery } = useImageGallery();
  const [galleryImages, setGalleryImages] = useState<Array<{ id: number | string; url: string; alt?: string; thumbnail?: string }>>([]);

  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [token, currentPage]);

  // Refresh products when navigating back to this page
  useEffect(() => {
    if (token && location.pathname === "/admin/products") {
      fetchProducts();
    }
  }, [location.pathname, token]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getProducts(token!, {
        page: currentPage,
        limit: productsPerPage,
      });
      // API function already handles wrapped response
      const productsList = data.products || data || [];
      setProducts(productsList);
      setTotalProducts(data.total || productsList.length);
    } catch (error: any) {
      console.error("Error fetching products:", error);
      toast.error(error.response?.data?.detail || "Failed to load products");
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (productId: number, currentStatus: boolean) => {
    if (!token) return;
    try {
      await adminApi.toggleProductActive(productId, token);
      toast.success(`Product ${currentStatus ? 'inactivated' : 'activated'} successfully`);
      // Refresh products list to show updated status
      await fetchProducts();
    } catch (error: any) {
      console.error("Error toggling product status:", error);
      toast.error(error.response?.data?.detail || "Failed to update product status");
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct || !token) return;
    try {
      await adminApi.deleteProduct(selectedProduct.id, token);
      toast.success("Product deleted successfully");
      setShowDeleteModal(false);
      setSelectedProduct(null);
      // If current page becomes empty after deletion, go to previous page
      if (products.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchProducts();
      }
    } catch (error: any) {
      console.error("Error deleting product:", error);
      toast.error(error.response?.data?.detail || "Failed to delete product");
    }
  };

  const totalPages = Math.ceil(totalProducts / productsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-1" />
            <div className="h-3 w-48 bg-gray-200 rounded-lg animate-pulse" />
          </div>
          <div className="w-1/2 h-9 bg-gray-200 rounded-full animate-pulse" />
          <div className="flex-shrink-0 flex gap-2 ml-auto">
            <div className="h-9 w-20 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-9 w-32 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <th key={i} className="px-4 py-2.5">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                        <div>
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-1" />
                          <div className="h-3 w-48 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                    </td>
                    {[1, 2, 3, 4, 5].map((j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const filteredProducts = products.filter((product) =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4">
      {/* Header, Search, and Add Button */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-xl font-bold font-playfair mb-0.5">Products</h1>
          <p className="text-xs text-gray-500">Manage your product catalog</p>
        </div>
        <div className="w-1/2 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="flex-shrink-0 ml-auto">
          <Link
            to="/admin/products/new"
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </Link>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    {searchTerm ? "No products match your search" : "No products found"}
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity group"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Collect product images
                            const productImages: Array<{ id: number | string; url: string; alt?: string; thumbnail?: string }> = [];
                            if (product.images && product.images.length > 0) {
                              productImages.push(...product.images.map((img: any) => ({
                                id: img.id || `${product.id}-${img.id}`,
                                url: img.image_url,
                                alt: img.alt_text || product.name,
                                thumbnail: img.image_url,
                              })));
                            } else if (product.image_url) {
                              productImages.push({
                                id: product.id,
                                url: product.image_url,
                                alt: product.name,
                                thumbnail: product.image_url,
                              });
                            } else {
                              // Fallback to placeholder
                              productImages.push({
                                id: product.id,
                                url: getSmartProductPlaceholder(product.name, "product"),
                                alt: product.name,
                                thumbnail: getSmartProductPlaceholder(product.name, "product"),
                              });
                            }
                            
                            if (productImages.length > 0) {
                              setGalleryImages(productImages);
                              openGallery(0);
                            }
                          }}
                        >
                          <img
                            src={
                              product.images?.[0]?.image_url ||
                              product.image_url ||
                              getSmartProductPlaceholder(product.name, "product")
                            }
                            alt={product.name}
                            
                            className="object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getSmartProductPlaceholder(product.name, "product");
                            }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            {product.is_price_frozen === true && (
                              <div 
                                className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0 border-2 border-white shadow-sm" 
                                title="Price Frozen"
                                style={{ minWidth: '12px', minHeight: '12px' }}
                              />
                            )}
                            <Link
                              to={`/admin/products/${product.id}`}
                              className="font-semibold text-sm text-gray-900 hover:text-aarsa-sage-600 transition-colors cursor-pointer"
                            >
                              {product.name}
                            </Link>
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-1 font-mono">
                            {product.product_code && product.slug 
                              ? `${product.product_code} • ${product.slug}`
                              : product.product_code || product.slug || "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-sm text-gray-900">
                          ₹{product.price || 0}
                        </p>
                        {product.original_price && product.original_price > product.price && (
                          <p className="text-xs text-gray-500 line-through">
                            ₹{product.original_price}
                          </p>
                        )}
                        {product.buyoutprice && product.price && product.price > 0 && (
                          <p className="text-xs font-medium mt-0.5" style={{
                            color: product.price > product.buyoutprice ? '#059669' : product.buyoutprice > product.price ? '#dc2626' : '#6b7280'
                          }}>
                            {(() => {
                              const earningsPercent = product.buyoutprice > 0 
                                ? (((product.price - product.buyoutprice) / product.price) * 100).toFixed(1)
                                : '0.0';
                              return `${earningsPercent}% earnings`;
                            })()}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          (product.stock || 0) > 10
                            ? "bg-green-100 text-green-700"
                            : (product.stock || 0) > 0
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {product.stock || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(product.id, product.is_active)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 ${
                            product.is_active
                              ? "bg-green-500"
                              : "bg-gray-300"
                          }`}
                          title={product.is_active ? "Click to inactivate" : "Click to activate"}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              product.is_active ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {product.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {product.created_at
                          ? new Date(product.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/products/${product.id}`}
                          className="group relative flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-full text-xs font-medium hover:bg-gray-900 hover:scale-105 transition-all duration-200"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">View</span>
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowDeleteModal(true);
                          }}
                          className="group relative flex items-center gap-1.5 px-3 py-1.5 bg-black text-white rounded-full text-xs font-medium hover:bg-gray-900 hover:scale-105 transition-all duration-200"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * productsPerPage + 1} to{" "}
            {Math.min(currentPage * productsPerPage, totalProducts)} of {totalProducts} products
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? "bg-gray-900 text-white"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-4 max-w-md w-full"
          >
            <h3 className="text-lg font-bold mb-3">Delete Product</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{selectedProduct.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedProduct(null);
                }}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-3 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Image Gallery */}
      {isOpen && galleryImages.length > 0 && (
        <ImageGallery
          images={galleryImages}
          initialIndex={initialIndex}
          isOpen={isOpen}
          onClose={closeGallery}
          showThumbnails={true}
        />
      )}
    </div>
  );
}
